import { useQueryClient } from "@tanstack/react-query";
import { ModelName, QueryMethodForModel, TellescopeQuery } from "../types";
import { useCallback, useSyncExternalStore, useRef } from "react";

/**
 * Configuration options for the useCachedTellescopeQueries hook.
 *
 * @template Model - The Tellescope model name to filter queries by
 * @template Method - Optional specific query method to filter by (defaults to all query methods for the model)
 * @template SelectedData - The type of data returned after applying the select function (defaults to array of queries)
 */
export interface UseCachedTellescopeQueriesArgs<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model> = QueryMethodForModel<Model>,
  SelectedData = TellescopeQuery<Model, Method>[],
> {
  /**
   * The Tellescope model name to filter cached queries by.
   * Only queries matching this model will be returned.
   *
   * @example "users" | "appointments" | "endusers"
   */
  model: Model;

  /**
   * Optional query method to further filter cached queries.
   * If provided, only queries matching both the model and method will be returned.
   * If omitted, all queries for the model will be returned regardless of method.
   *
   * @example "getOne" | "getSome" | "getByIds"
   */
  method?: Method;

  /**
   * Optional transformation function to process the array of cached queries.
   * Use this to extract specific data, filter queries by state, aggregate results, etc.
   *
   * **IMPORTANT**: This function must be STABLE (memoized with `useCallback` or defined outside the component).
   * Additionally, for complex transformations that return objects or arrays, you should use `useMemo` on the
   * hook's result to cache the transformed data, since `useSyncExternalStore` may call this function frequently.
   *
   * **For simple cases** (like counting): Return primitive values directly from select
   * **For complex transformations**: Use a separate `useMemo` to cache the result after calling the hook
   *
   * Common use cases:
   * - Count queries (safe): `(queries) => queries.length`
   * - Extract data (needs additional caching): `(queries) => queries.map(q => q.state.data)`
   *
   * @param queries - Array of TanStack Query objects matching the model/method filter
   * @returns Transformed data of any type
   */
  select?: (queries: TellescopeQuery<Model, Method>[]) => SelectedData;
}

/**
 * Return value from the useCachedTellescopeQueries hook.
 *
 * @template Data - The type of data being returned (either raw queries or transformed via select function)
 */
export interface UseCachedTellescopeQueriesResult<Data> {
  /**
   * The cached query data, either as raw TanStack Query objects or transformed via the select function.
   * Automatically updates when the query cache changes (queries added, updated, or removed).
   */
  data: Data;

  /**
   * Function to invalidate all queries for the specified model, triggering cache updates and refetches.
   * Useful when you know data has changed externally and want to force a refresh.
   *
   * Note: This invalidates ALL queries for the model, not just those matching the method filter.
   *
   * @example
   * ```ts
   * const { data, refresh } = useCachedTellescopeQueries({ model: "users" });
   * // After an external mutation:
   * refresh(); // Invalidates all "users" queries
   * ```
   */
  refresh: () => void;
}

/**
 * React hook that provides reactive access to cached Tellescope queries from the TanStack Query cache.
 *
 * This hook uses `useSyncExternalStore` to subscribe to the query cache, automatically
 * re-rendering whenever matching queries are added, updated, or removed. It's useful for:
 * - Displaying real-time query status across your application
 * - Building developer tools or debugging UIs
 * - Implementing custom cache management logic
 * - Aggregating data from multiple cached queries
 *
 * Key features:
 * - **Reactive**: Automatically updates when cache changes
 * - **Flexible filtering**: Filter by model, method, or both
 * - **Transformation**: Use select function to extract or aggregate data
 * - **Type-safe**: Full TypeScript inference for model and method
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 * @template SelectedData - The type of transformed data returned
 *
 * @param args - Configuration object with model, optional method, and optional select function
 * @returns Object containing the cached query data and a refresh function
 *
 * @example
 * // Get all cached queries for users model
 * const { data: userQueries } = useCachedTellescopeQueries({
 *   model: "users"
 * });
 *
 * @example
 * // Get only specific method queries
 * const { data: getOneQueries } = useCachedTellescopeQueries({
 *   model: "users",
 *   method: "getOne"
 * });
 *
 * @example
 * // Extract data from successful queries (with memoized select function)
 * const selectFn = useCallback((queries) => queries
 *   .filter(q => q.state.status === "success")
 *   .map(q => q.state.data)
 *   .filter((data): data is NonNullable<typeof data> => data != null), []);
 *
 * const { data: users } = useCachedTellescopeQueries({
 *   model: "users",
 *   method: "getOne",
 *   select: selectFn
 * });
 *
 * @example
 * // Count loading queries (with memoized select function)
 * const countPending = useCallback(
 *   (queries) => queries.filter(q => q.state.status === "pending").length,
 *   []
 * );
 *
 * const { data: loadingCount } = useCachedTellescopeQueries({
 *   model: "appointments",
 *   select: countPending
 * });
 *
 * @example
 * // Use refresh to invalidate queries
 * const { data, refresh } = useCachedTellescopeQueries({ model: "users" });
 * const handleExternalUpdate = () => {
 *   refresh(); // Invalidates all "users" queries
 * };
 */
export const useCachedTellescopeQueries = <
  Model extends ModelName,
  Method extends QueryMethodForModel<Model> = QueryMethodForModel<Model>,
  SelectedData = TellescopeQuery<Model, Method>[],
>({
  model,
  method,
  select,
}: UseCachedTellescopeQueriesArgs<Model, Method, SelectedData>): UseCachedTellescopeQueriesResult<SelectedData> => {
  const queryClient = useQueryClient();
  const queryCache = queryClient.getQueryCache();

  // Cache the last snapshot to avoid unnecessary re-renders when data hasn't actually changed
  const snapshotRef = useRef<SelectedData | undefined>(undefined);

  const subscribe = useCallback((onStoreChange: () => void) => queryCache.subscribe(onStoreChange), [queryCache]);

  const getSnapshot = useCallback(() => {
    // Get matching queries from cache
    const queries = queryCache.findAll({
      queryKey: method ? [model, method] : [model],
      exact: false,
    }) as unknown as TellescopeQuery<Model, Method>[];

    // Apply select function if provided, otherwise return queries as-is
    const newSnapshot = (select ? select(queries) : queries) as SelectedData;

    // For primitive values or when select returns a stable reference, compare by reference
    // For arrays/objects without select, compare query count and updatedAt timestamps
    const shouldUpdate = (() => {
      const prevSnapshot = snapshotRef.current;

      // First render or explicit undefined
      if (prevSnapshot === undefined) return true;

      // If select function is provided, we need to handle different return types
      if (select) {
        // For primitive values, use referential equality
        if (typeof newSnapshot !== "object" || newSnapshot === null) {
          return newSnapshot !== prevSnapshot;
        }

        // For arrays (common case for select functions), do shallow comparison
        if (Array.isArray(newSnapshot) && Array.isArray(prevSnapshot)) {
          if (newSnapshot.length !== prevSnapshot.length) return true;

          // Compare array elements
          for (let i = 0; i < newSnapshot.length; i++) {
            if (newSnapshot[i] !== prevSnapshot[i]) {
              return true;
            }
          }

          return false;
        }

        // For Sets, compare size and elements
        if (newSnapshot instanceof Set && prevSnapshot instanceof Set) {
          if (newSnapshot.size !== prevSnapshot.size) return true;
          for (const item of newSnapshot) {
            if (!prevSnapshot.has(item)) return true;
          }
          return false;
        }

        // For Maps, compare size and entries
        if (newSnapshot instanceof Map && prevSnapshot instanceof Map) {
          if (newSnapshot.size !== prevSnapshot.size) return true;
          for (const [key, value] of newSnapshot) {
            if (!prevSnapshot.has(key) || prevSnapshot.get(key) !== value) return true;
          }
          return false;
        }

        // For other objects, fall back to referential equality
        return JSON.stringify(newSnapshot) !== JSON.stringify(prevSnapshot);
      }

      // For arrays of queries (no select), do a shallow comparison
      if (Array.isArray(newSnapshot) && Array.isArray(prevSnapshot)) {
        // Different length = different data
        if (newSnapshot.length !== prevSnapshot.length) return true;

        // Check if any query has been updated by comparing dataUpdatedAt timestamps
        for (let i = 0; i < newSnapshot.length; i++) {
          const newQuery = newSnapshot[i];
          const prevQuery = prevSnapshot[i];

          if (
            newQuery?.state?.dataUpdatedAt !== prevQuery?.state?.dataUpdatedAt ||
            newQuery?.queryHash !== prevQuery?.queryHash
          ) {
            return true;
          }
        }

        return false;
      }

      // For other types, use referential equality
      return newSnapshot !== prevSnapshot;
    })();

    // Only update the ref if data actually changed
    if (shouldUpdate) {
      snapshotRef.current = newSnapshot;
    }

    // Always return the cached reference to maintain stability
    return snapshotRef.current as SelectedData;
  }, [queryCache, model, method, select]);

  const data = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const refresh = useCallback(() => {
    // Invalidate queries to trigger cache update
    queryClient.invalidateQueries({ queryKey: [model] });
  }, [queryClient, model]);

  return { data, refresh };
};
